import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    MdConfirmationNumber,
    MdInbox,
    MdMenuBook,
    MdSupportAgent,
} from 'react-icons/md'
import {
    getHelpCenterCapabilities,
    type HelpCenterCapabilities,
} from '@/lib/api/help-center/help-center-api'
import TicketsTab from './tickets-tab'
import ArticlesTab from './articles-tab'

type TabKey = 'my-tickets' | 'all-tickets' | 'articles'

const emptyCapabilities: HelpCenterCapabilities = {
    isAgent: false,
    canReadAllTickets: false,
    canManageTickets: false,
    canCreateArticles: false,
    canUpdateArticles: false,
    canDeleteArticles: false,
}

export default function SupportPage() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [activeTab, setActiveTab] = useState<TabKey>(
        searchParams.get('tab') === 'articles' ? 'articles' : 'my-tickets'
    )
    const [capabilities, setCapabilities] =
        useState<HelpCenterCapabilities>(emptyCapabilities)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let mounted = true
        getHelpCenterCapabilities()
            .then(res => {
                if (mounted && res.success && res.result) {
                    setCapabilities(res.result)
                }
            })
            .finally(() => {
                if (mounted) setLoading(false)
            })
        return () => {
            mounted = false
        }
    }, [])

    const tabs = useMemo(() => {
        const list: { key: TabKey; label: string; icon: typeof MdInbox }[] = [
            {
                key: 'my-tickets',
                label: 'Meus Tickets',
                icon: MdConfirmationNumber,
            },
        ]
        if (capabilities.canReadAllTickets) {
            list.push({
                key: 'all-tickets',
                label: 'Todos os Tickets',
                icon: MdInbox,
            })
        }
        list.push({ key: 'articles', label: 'Artigos', icon: MdMenuBook })
        return list
    }, [capabilities.canReadAllTickets])

    const changeTab = (tab: TabKey) => {
        setActiveTab(tab)
        const next = new URLSearchParams(searchParams)
        next.set('tab', tab)
        next.delete('ticket')
        next.delete('article')
        setSearchParams(next, { replace: true })
    }

    return (
        <div className="flex flex-col gap-6 max-w-5xl mx-auto w-full">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="bg-secondary/10 text-secondary rounded-xl p-3">
                    <MdSupportAgent className="text-2xl" />
                </div>
                <div>
                    <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                        Centro de Ajuda e Suporte
                    </h1>
                    <p className="text-sm text-base-content/60">
                        Abra tickets para o suporte e consulte artigos e
                        tutoriais
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs tabs-boxed bg-base-200/50 p-1 rounded-xl w-full sm:w-fit gap-5">
                {tabs.map(tab => {
                    const Icon = tab.icon
                    return (
                        <button
                            key={tab.key}
                            className={`tab flex-1 !flex-row flex-nowrap items-center gap-2 whitespace-nowrap ${
                                activeTab === tab.key
                                    ? 'tab-active bg-secondary text-primary-content'
                                    : ''
                            }`}
                            onClick={() => changeTab(tab.key)}
                        >
                            <Icon className="shrink-0" />
                            <span>{tab.label}</span>
                        </button>
                    )
                })}
            </div>

            {loading ? (
                <div className="flex justify-center py-20">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : activeTab === 'articles' ? (
                <ArticlesTab capabilities={capabilities} />
            ) : (
                <TicketsTab
                    capabilities={capabilities}
                    scope={activeTab === 'all-tickets' ? 'all' : 'mine'}
                />
            )}
        </div>
    )
}
